import { useNavigate } from "react-router-dom";
import { eventDetails, EventDetail } from "@/mocks/eventDetails";

interface Props {
  currentEvent: EventDetail;
}

const slugMap: Record<number, string> = {
  1: "glamping-party",
  2: "book-club",
  3: "social-game",
  4: "family-camp",
};

export default function RelatedEvents({ currentEvent }: Props) {
  const navigate = useNavigate();

  const related = eventDetails
    .filter((e) => e.id !== currentEvent.id)
    .slice(0, 3);

  return (
    <section className="py-16 bg-white border-t border-gray-100">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-red-600 text-xs font-semibold tracking-widest uppercase mb-2">
              More Events
            </p>
            <h2
              className="text-2xl md:text-3xl font-black text-gray-900"
              style={{ fontFamily: "'Noto Serif TC', serif" }}
            >
              你可能也喜歡
            </h2>
          </div>
          <button
            onClick={() => {
              navigate("/");
              setTimeout(() => {
                const el = document.querySelector("#upcoming-events");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }, 300);
            }}
            className="hidden sm:inline-flex items-center gap-1.5 text-sm text-red-600 font-semibold hover:gap-3 transition-all duration-200 cursor-pointer group whitespace-nowrap"
          >
            查看所有活動
            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-200" />
          </button>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          {related.map((event) => (
            <div
              key={event.id}
              onClick={() => {
                navigate(`/event/${slugMap[event.id]}`);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className="group rounded-2xl overflow-hidden border border-gray-100 cursor-pointer hover:-translate-y-1 transition-transform duration-300"
            >
              {/* Image */}
              <div className="relative overflow-hidden" style={{ height: "200px" }}>
                <img
                  src={event.heroImage}
                  alt={event.title}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                {/* Category badge */}
                <span className={`absolute top-3 left-3 ${event.categoryColor} text-white text-xs font-semibold px-3 py-1 rounded-full`}>
                  {event.category}
                </span>
                {/* Spots left */}
                {event.spotsLeft <= 10 && (
                  <span className="absolute top-3 right-3 bg-black/60 text-orange-300 text-xs font-semibold px-2.5 py-1 rounded-full">
                    剩 {event.spotsLeft} 名額
                  </span>
                )}
              </div>

              {/* Content */}
              <div className="p-5">
                <h3 className="font-bold text-gray-900 text-base mb-1 leading-snug group-hover:text-red-600 transition-colors duration-200">
                  {event.title}
                </h3>
                <p className="text-gray-400 text-xs mb-3">{event.subtitle}</p>

                <div className="flex flex-col gap-1.5 mb-4">
                  <div className="flex items-center gap-1.5 text-gray-500 text-xs">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-calendar-line" />
                    </span>
                    {event.date}{event.endDate ? ` ～ ${event.endDate}` : ""}
                  </div>
                  <div className="flex items-center gap-1.5 text-gray-500 text-xs">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-map-pin-line" />
                    </span>
                    {event.location}
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-red-600 font-bold text-sm">{event.priceLabel}</span>
                  <span className="inline-flex items-center gap-1 text-xs text-gray-400 font-medium group-hover:text-red-500 transition-colors duration-200">
                    了解詳情
                    <i className="ri-arrow-right-line" />
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile view all */}
        <div className="mt-8 text-center sm:hidden">
          <button
            onClick={() => {
              navigate("/");
              setTimeout(() => {
                const el = document.querySelector("#upcoming-events");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }, 300);
            }}
            className="inline-flex items-center gap-2 text-red-600 font-semibold text-sm cursor-pointer"
          >
            查看所有活動
            <i className="ri-arrow-right-line" />
          </button>
        </div>
      </div>
    </section>
  );
}
